import { css, cx } from "@emotion/css";
import { Badge, resetStyles, Tooltip, useIsHovered } from "@octopusdeploy/design-system-components";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import React, { useRef } from "react";
import type { ProcessOverviewStepData } from "~/areas/projects/components/Process/ProcessOverview/ProcessOverview";
import { isStepContainer } from "~/areas/projects/components/Process/ProcessOverview/ProcessOverview";
import type { ProcessOverviewStepBaseData } from "~/areas/projects/components/Process/ProcessOverview/ProcessOverviewStepAction";
import { ProcessOverviewStepAction } from "~/areas/projects/components/Process/ProcessOverview/ProcessOverviewStepAction";
import { RunInParallelIndicator } from "~/areas/projects/components/Process/RunInParallelIndicator";
import InternalLink from "~/components/Navigation/InternalLink";
export interface ProcessOverviewStepContainerData extends ProcessOverviewStepBaseData {
    containerLabel?: "Rolling" | "Template";
    containerSteps: ProcessOverviewStepData[];
}
export interface ProcessOverviewStepContainerProps {
    stepContainerData: ProcessOverviewStepContainerData;
    showNote: boolean;
    isRootStep: boolean;
}
export function ProcessOverviewStepContainer({ stepContainerData, showNote, isRootStep }: ProcessOverviewStepContainerProps) {
    const { name, logo, logoTooltip, href, summary, isDisabled, containerLabel, containerSteps } = stepContainerData;
    const headerRef = useRef<HTMLAnchorElement>(null);
    const isHeaderHovered = useIsHovered(headerRef);
    return (<div className={cx(styles.root, { [styles.hover]: isHeaderHovered, [styles.rootStep]: isRootStep })}>
            {stepContainerData.isRunInParallelWithPreviousStep && <RunInParallelIndicator />}
            <InternalLink ref={headerRef} className={styles.stepHeader} to={href}>
                <Tooltip className={styles.iconContainer} content={logoTooltip}>
                    {"src" in logo ? <img className={styles.icon} src={logo.src} alt=""/> : logo}
                </Tooltip>
                <div className={cx(styles.stepNameAndSummary, { [styles.disabled]: isDisabled })}>
                    <span className={cx(styles.name, { [styles.nameDisabled]: isDisabled })}>{name}</span>
                    <div>{summary}</div>
                </div>
                {containerLabel && <Badge size={"small"} variant={"neutral"} label={containerLabel}/>}
            </InternalLink>
            <ol className={styles.childSteps}>
                {containerSteps.map((stepData) => (<li key={stepData.id}>
                        {isStepContainer(stepData) ? (<ProcessOverviewStepContainer key={stepData.id} stepContainerData={stepData} showNote={showNote} isRootStep={false}/>) : (<ProcessOverviewStepAction key={stepData.id} stepActionData={stepData} showNote={showNote} isRootStep={false}/>)}
                    </li>))}
            </ol>
        </div>);
}
const styles = {
    root: css({
        position: "relative",
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: space[8],
        border: `${borderWidth[1]} solid ${themeTokens.color.processStep.border.default}`,
        backgroundColor: themeTokens.color.processStep.background.primary.default,
        borderRadius: borderRadius.small,
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        transitionProperty: "background-color, border-color",
        transitionDuration: "0.1s",
        ":has(> a:first-child:focus-visible)": {
            boxShadow: themeTokens.shadow.focused,
        },
    }),
    disabled: css({
        opacity: 0.5,
    }),
    hover: css({
        borderColor: themeTokens.color.processStep.border.hover,
        backgroundColor: themeTokens.color.processStep.background.primary.hover,
    }),
    stepHeader: css({
        ...resetStyles.anchor,
        display: "flex",
        gap: space[8],
        padding: `${space[12]} ${space[12]} 0`,
        "&:hover": {
            color: themeTokens.color.text.primary, // prevent global link hover styles applying
        },
    }),
    iconContainer: css({
        background: colorScales.white,
        borderRadius: borderRadius.small,
        alignContent: "center",
        height: "2.5rem",
        width: "2.5rem",
        overflow: "clip",
    }),
    icon: css({
        display: "block",
        objectFit: "scale-down",
        width: "100%",
    }),
    stepNameAndSummary: css({
        flex: 1,
    }),
    name: css({
        font: text.body.bold.medium,
    }),
    nameDisabled: css({
        textDecoration: "line-through",
    }),
    rootStep: css({
        boxShadow: themeTokens.shadow.extraSmall,
    }),
    childSteps: css({
        ...resetStyles.list,
        display: "flex",
        flexDirection: "column",
        padding: `0 ${space[12]} ${space[12]}`,
        gap: space[8],
    }),
};
